import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import chatApiService from '../services/api';
import { useChatApi } from '../contexts/ChatApiContext';

interface ConnectionStatusProps {
  redirectOnConnect?: boolean;
  redirectPath?: string;
  showTest?: boolean;
}

const ConnectionStatus: React.FC<ConnectionStatusProps> = ({
  redirectOnConnect = false,
  redirectPath = '/alex',
  showTest = false
}) => {
  const { isConnected: contextConnected, isConnecting: contextConnecting, connect, disconnect } = useChatApi();

  const [isConnected, setIsConnected] = useState<boolean>(contextConnected);
  const [connecting, setConnecting] = useState<boolean>(contextConnecting);
  const [testMessage, setTestMessage] = useState<string>('');
  const [testResponse, setTestResponse] = useState<string | null>(null);

  const navigate = useNavigate();

  // Update local state when context connection state changes
  useEffect(() => {
    console.log('ConnectionStatus: Context connection status updated:', contextConnected);
    setIsConnected(contextConnected);

    if (contextConnected && redirectOnConnect) {
      console.log('ConnectionStatus: Connected, redirecting to', redirectPath);
      navigate(redirectPath);
    }
  }, [contextConnected, navigate, redirectOnConnect, redirectPath]);

  // Update connecting state when context connecting state changes
  useEffect(() => {
    console.log('ConnectionStatus: Context connecting status updated:', contextConnecting);
    setConnecting(contextConnecting);
  }, [contextConnecting]);

  // Subscribe to test messages
  useEffect(() => {
    if (!showTest) return;

    // Subscribe to incoming messages for the test feature
    const messageUnsubscribe = chatApiService.onMessage((message) => {
      setTestResponse(message.content);
    });

    return () => {
      messageUnsubscribe();
    };
  }, [showTest]);

  const handleConnect = () => {
    console.log('ConnectionStatus: Connecting');
    // No need to setConnecting(true) here anymore, as it will be set by the context
    connect();
  };

  const handleDisconnect = () => {
    console.log('ConnectionStatus: Disconnecting');
    disconnect();
  };

  const handleSendTest = () => {
    if (testMessage.trim() && isConnected) {
      chatApiService.sendMessage(testMessage);
      setTestMessage('');
    }
  };

  return (
    <div className="border rounded-lg p-4 bg-white shadow">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">Connection Status</h3>
        <div className="flex items-center">
          <div className={`h-3 w-3 rounded-full mr-2 ${isConnected ? 'bg-green-500' : connecting ? 'bg-yellow-500 animate-pulse' : 'bg-red-500'}`}></div>
          <span className={`text-sm ${isConnected ? 'text-green-600' : connecting ? 'text-yellow-600' : 'text-red-600'}`}>
            {isConnected ? 'Connected' : connecting ? 'Connecting...' : 'Disconnected'}
          </span>
        </div>
      </div>

      <div className="flex space-x-2 mb-4">
        {!isConnected ? (
          <button
            onClick={handleConnect}
            disabled={connecting}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300 flex-1 transition-colors"
          >
            {connecting ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Connecting...
              </span>
            ) : 'Connect to AI Service'}
          </button>
        ) : (
          <button
            onClick={handleDisconnect}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex-1 transition-colors"
          >
            Disconnect
          </button>
        )}

        {isConnected && redirectOnConnect && (
          <button
            onClick={() => navigate(redirectPath)}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex-1 transition-colors"
          >
            Go to Chat
          </button>
        )}
      </div>

      {showTest && isConnected && (
        <div>
          <div className="flex space-x-2 mb-2">
            <input
              type="text"
              value={testMessage}
              onChange={(e) => setTestMessage(e.target.value)}
              placeholder="Enter a test message"
              className="flex-1 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              onKeyDown={(e) => e.key === 'Enter' && handleSendTest()}
            />
            <button
              onClick={handleSendTest}
              disabled={!testMessage.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-300"
            >
              Send
            </button>
          </div>

          {testResponse && (
            <div className="mt-2 p-3 bg-gray-100 rounded text-sm">
              <div className="font-medium mb-1">Response:</div>
              <div className="text-gray-700 whitespace-pre-wrap">{testResponse}</div>
            </div>
          )}
        </div>
      )}

      <div className="text-xs text-gray-500 mt-4">
        <code className="bg-gray-100 px-1 py-0.5 rounded text-gray-600 text-xs break-all">
          wss://7pumfc2f80.execute-api.eu-west-1.amazonaws.com/dev
        </code>
      </div>
    </div>
  );
};

export default ConnectionStatus;